import React, { FC, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Box, CircularProgress, Stack, Typography, useTheme } from '@mui/material';

import { OrganizationDTOStatus } from '@/api/generated';
import OrganizationImagePlaceholder from '@/assets/icons/org-image-placeholder.svg?react';
import BackdropLoader from '@/components/BackdropLoader';
import ErrorAccessDenied from '@/components/ErrorAccessDenied/ErrorAccessDenied';
import ErrorGeneric from '@/components/ErrorGeneric/ErrorGeneric';
import ButtonGroupToggle from '@/components/fields/ButtonGroupToggle';
import ChipSelect from '@/components/fields/ChipSelect';
import Field from '@/components/fields/Field';
import GeneralStatusDropdownSelect from '@/components/fields/GeneralStatusDropdownSelect';
import TextInput from '@/components/fields/TextInput';
import { PermissionGuard } from '@/components/PermissionGuard';
import { useIsMidotUser } from '@/hooks/permissions/useIsMidotUser';
import { useOrganizationPermissions } from '@/hooks/permissions/useOrganizationPermissions';
import { PermissionKeys } from '@/hooks/permissions/usePermissions';
import { useOrganizationStatusActions } from '@/hooks/useOrganizationStatusActions';
import { ReadonlyProvider } from '@/hooks/useReadonly';
import typography from '@/theme/typography';
import CountryDropdownSelect from '@/views/fields/CountryDropdownSelect/CountryDropdownSelect';
import IndustryDropdownSelect from '@/views/fields/IndustryDropdownSelect/IndustryDropdownSelect';
import OfficeDropdownSelect from '@/views/fields/OfficeDropdownSelect/OfficeDropdownSelect';
import MidotUserDropdownSelect from '@/views/fields/UserDropdownSelect/MidotUserDropdownSelect';

import { getNumberOfEmployeesOptions } from './basicOptions';

const BasicInfoTab: FC = () => {
  const { t } = useTranslation('settings');
  const { spacing } = useTheme();
  const { watch, setValue } = useFormContext();

  const isEnterprise = watch('isEnterprise');

  const { organizationSlug } = useParams() as { organizationSlug: string };
  const currentStatus = watch('status');

  const setStatus = useCallback(
    (newStatus: OrganizationDTOStatus) => {
      setValue('status', newStatus);
    },
    [setValue]
  );

  const {
    change,
    isLoading: isStatusLoading,
    isFinalState,
    availableStatuses
  } = useOrganizationStatusActions({
    currentStatus,
    onChange: setStatus,
    organizationSlug
  });

  const { value: { canUpdateBasicInfo } = {} } = useOrganizationPermissions({
    variables: { organizationSlug }
  });

  const { isMidotUser } = useIsMidotUser();

  const handleChange = useCallback(
    (newStatus: string) => {
      change(newStatus as OrganizationDTOStatus);
    },
    [change]
  );

  return (
    <PermissionGuard
      all={[PermissionKeys.OrganizationcanViewBasicInfo]}
      variables={{ organizationSlug }}
      accessDeniedFallback={<ErrorAccessDenied />}
      errorFallback={<ErrorGeneric />}
      loadingFallback={<BackdropLoader />}
    >
      <ReadonlyProvider readonly={!canUpdateBasicInfo}>
        <Stack
          width="100%"
          sx={{
            '& .MuiFormLabel-root': { textAlign: 'start' },
            '& .MuiFormControl-root': { pb: 0 },
            '& .DropdownSelect-expandMoreIcon': { color: 'text.primary' }
          }}
        >
          <Stack direction="row" justifyContent="space-between" width="100%" p={3}>
            <Typography sx={{ ...typography.body1, fontWeight: typography.fontWeightMedium }}>
              {t('orgSettings.tabs.basicInfo')}
            </Typography>
            <Stack direction="row" alignItems="center" gap={1}>
              <Typography sx={{ ...typography.body3, fontWeight: typography.fontWeightMedium, color: 'grey.700' }}>
                {t('orgSettings.fields.status')}
              </Typography>
              <Box>
                <Stack direction="row" alignItems="center" gap={1}>
                  <GeneralStatusDropdownSelect
                    name="status"
                    onChange={handleChange}
                    value={currentStatus}
                    activeOptions={availableStatuses as string[]}
                    isDisabled={isFinalState || !isMidotUser}
                    sx={{
                      mx: 2,
                      '& .DropdownSelect-control': {
                        minWidth: 122,
                        minHeight: 'unset',
                        '& .DropdownSelect-optionIcon .CircleIcon-root': {
                          m: 0.25
                        },
                        '& .DropdownSelect-optionText .MuiTypography-root': {
                          ...typography.body3,
                          lineHeight: 'calc(4 / 3)'
                        }
                      },
                      '& .DropdownSelect-indicator': {
                        p: 0.5
                      }
                    }}
                  />
                  {isStatusLoading && <CircularProgress size={20} />}
                </Stack>
              </Box>
            </Stack>
          </Stack>
          <Stack px={3} mb={3} gap={3} direction="row" alignItems="end">
            <OrganizationImagePlaceholder />
            {isMidotUser && (
              <ButtonGroupToggle
                value={isEnterprise}
                prefix="orgSettings.options.enterprise"
                ns="settings"
                disabled
                sx={{
                  flexDirection: 'row-reverse'
                }}
              />
            )}
          </Stack>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              mx: 3,
              gridColumnGap: spacing(2),
              gridRowGap: spacing(3),
              '& .MuiInputBase-root': { minHeight: spacing(5) },
              '& .MuiFormControl-root': { px: 0, pt: 0 },
              '& .DropdownSelect-control': { minHeight: spacing(5) }
            }}
          >
            <Field
              component={TextInput}
              name="name"
              label={t(`orgSettings.fields.organizationName`)}
              innerProps={{
                sx: {
                  '& .MuiInputAdornment-root': { display: 'none' }
                }
              }}
              withFormControl
            />
            <Field
              component={TextInput}
              name="legalName"
              label={t('orgSettings.fields.legalName')}
              innerProps={{
                sx: {
                  '& .MuiInputAdornment-root': { display: 'none' }
                }
              }}
              withFormControl
            />

            <Field
              component={IndustryDropdownSelect}
              name="industry"
              label={t('orgSettings.fields.industry')}
              innerProps={{
                isMulti: false,
                usePortal: true
              }}
              withFormControl
            />
            {isMidotUser && (
              <Field
                component={OfficeDropdownSelect}
                name="offices"
                label={t('orgSettings.fields.office')}
                withFormControl
                innerProps={{ usePortal: true }}
              />
            )}
            <Field
              component={CountryDropdownSelect}
              name="countries"
              label={t(`orgSettings.fields.orgCountry`)}
              withFormControl
              innerProps={{ usePortal: true, isDisabled: !isMidotUser || !canUpdateBasicInfo }}
            />
            <Field
              component={MidotUserDropdownSelect}
              name="keyAccountManagers"
              label={t('orgSettings.fields.keyAccountManager')}
              withFormControl
              innerProps={{ usePortal: true, isMulti: true, isDisabled: !isMidotUser || !canUpdateBasicInfo }}
            />
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              mt: 3,
              mx: 3,
              gridColumnGap: spacing(2),
              gridRowGap: spacing(3),
              '& .MuiInputBase-root': { minHeight: spacing(5) },
              '& .MuiFormControl-root': { px: 0, pt: 0 },
              '& .DropdownSelect-control': { minHeight: spacing(5) }
            }}
          >
            <Field
              component={ChipSelect}
              name="employeeCount"
              label={t('orgSettings.fields.numberOfEmployees')}
              innerProps={{
                options: getNumberOfEmployeesOptions(t),
                sx: { py: 0.25, mx: -0.5, '& .MuiButtonBase-root': { px: 1.25 } }
              }}
              withFormControl
              outerSx={{ gridColumn: 'span 2' }}
            />
          </Box>
          {isMidotUser && (
            <>
              <Box
                sx={{
                  display: 'grid',
                  width: '67%',
                  gridTemplateColumns: '1fr 1fr',
                  mx: 3,
                  mt: 3,
                  gridColumnGap: spacing(2),
                  gridRowGap: spacing(3),
                  '& .MuiFormControl-root': { px: 0, pt: 0 },
                  '& .MuiInputBase-root': { minHeight: spacing(5) }
                }}
              >
                <Field
                  component={TextInput}
                  name="accountNumbers.newId"
                  label={t('orgSettings.fields.accountNumbersNewId')}
                  innerProps={{
                    sx: {
                      '& .MuiInputAdornment-root': { display: 'none' },
                      '& .MuiInputBase-root': {
                        bgcolor: 'grey.200'
                      },
                      '& .MuiInputBase-root:hover fieldset': {
                        borderColor: 'grey.400'
                      },
                      color: 'grey.500'
                    },
                    disabled: true
                  }}
                  withFormControl
                />
                <Field
                  component={TextInput}
                  name="legacyId"
                  label={t('orgSettings.fields.accountNumbersOldId')}
                  innerProps={{
                    sx: {
                      '& .MuiInputAdornment-root': { display: 'none' }
                    },
                    numbersOnly: true
                  }}
                  withFormControl
                />
                <Field
                  component={TextInput}
                  name="jiraId"
                  label={t('orgSettings.fields.accountNumbersJiraId')}
                  innerProps={{
                    sx: {
                      '& .MuiInputAdornment-root': { display: 'none' }
                    },
                    numbersOnly: true
                  }}
                  withFormControl
                />

                <Field
                  component={TextInput}
                  name="pipedriveId"
                  label={t('orgSettings.fields.accountNumbersPipedriveId')}
                  innerProps={{
                    sx: {
                      '& .MuiInputAdornment-root': { display: 'none' }
                    },
                    numbersOnly: true
                  }}
                  withFormControl
                />
              </Box>
            </>
          )}
        </Stack>
      </ReadonlyProvider>
    </PermissionGuard>
  );
};

export default BasicInfoTab;
